/**
 * useTourProgress — Hook para sincronizar progreso del tour con el backend.
 * Sprint 2: Product Tour
 *
 * Uso:
 *   const { records, loading, recordStep, hasCompleted } = useTourProgress();
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/axiosInstance';

const API_BASE = '/tour';

/**
 * Obtiene todos los registros de tour del usuario.
 * Permite saber si un tour (mode+length) ya fue completado.
 */
export const useTourProgress = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`${API_BASE}/progress`);
      setRecords(res.data ?? []);
    } catch (err) {
      // Silent fail — no bloquear UI si BD no responde
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  /**
   * Registrar un paso visto. Llama POST /api/tour/progress.
   * @param {string} mode - 'prospect' | 'onboarding'
   * @param {string} length - 'quick' | 'full'
   * @param {string} stepId - ID del paso actual (o 'complete' al terminar)
   * @param {string[]} stepsSeen - array acumulado de IDs vistos
   */
  const recordStep = useCallback(async (mode, length, stepId, stepsSeen = []) => {
    try {
      const res = await apiClient.post(`${API_BASE}/progress`, {
        mode,
        length,
        step_id: stepId,
        steps_seen: stepsSeen,
      });
      // Actualizar cache local
      setRecords(prev => {
        const idx = prev.findIndex(
          r => r.mode === mode && r.length === length
        );
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = res.data;
          return next;
        }
        return [...prev, res.data];
      });
      return res.data;
    } catch (err) {
      // silencio-ok: telemetría del progreso del tour; el tour sigue funcionando sin BD
      console.warn('[useTourProgress] recordStep failed:', err?.message);
      return null;
    }
  }, []);

  /**
   * Devuelve true si el usuario ya completó el tour (mode+length).
   */
  const hasCompleted = useCallback(
    (mode, length) => {
      const record = records.find(r => r.mode === mode && r.length === length);
      return record?.completed_at != null;
    },
    [records]
  );

  return { records, loading, error, recordStep, hasCompleted, refetch: fetchProgress };
};

export default useTourProgress;
